import React from 'react';
import { motion } from 'framer-motion';
import { getTechIcon } from '../data/techIcons';
import './Projects.css';
import { FaLock, FaShieldAlt } from 'react-icons/fa';

interface Project {
  title: string;
  description: string;
  image: string;
  technologies: string[];
  link?: string;
  isNDA?: boolean;
  confidentialityLevel?: 'high' | 'medium' | 'low';
}

const projects: Project[] = [
  {
    title: 'SinewsLink',
    description: 'Leading a tech company focused on AI and machine learning solutions. Developed innovative AI/ML solutions for business process optimization.',
    image: '/images/sinewslink.gif',
    technologies: ['Python', 'TensorFlow', 'AWS', 'React', 'Node.js', 'CI/CD'],
    link: 'https://sinewslink.com'
  },
  {
    title: 'SL Aegis',
    description: 'Canada-first geospatial decision-support platform for wildfire, flood and earthquake response. Combines live hazard feeds (fire hotspots, evacuation orders, weather, air quality, smoke) with communities, infrastructure, road events and shelter capacity.',
    image: '/images/aegis.png',
    technologies: ['Next.js', 'React', 'Geospatial', 'Real-time Processing'],
    link: 'https://aegis.sinewslink.com'
  },
  {
    title: 'Loupe',
    description: 'Engineering-impact analytics for GitHub repositories. Pulls contribution data through the GitHub GraphQL API and generates LLM-written insights and recommended actions, backed by tests, Docker and architecture decision records.',
    image: '/images/loupe.png',
    technologies: ['Python', 'React', 'TypeScript', 'Docker'],
    link: 'https://github.com/Anyaoha/loupe'
  },
  {
    title: 'Enterprise LLM Gateway & Observability',
    description: 'Centralized multi-provider LLM routing (Azure OpenAI, Bedrock, self-hosted GPU models) with automatic failover and per-team token budgets. OpenTelemetry-native observability with a shared collector template, gateway-enforced redaction and a canonical span-attribute standard adopted across three product teams, plus RAGAS drift detection. Cut ML deployment time from 45 days to under 5 minutes.',
    image: '/images/cloud.jpg',
    technologies: ['OpenTelemetry', 'AWS', 'Azure', 'Terraform', 'Datadog', 'MLflow'],
    isNDA: true,
    confidentialityLevel: 'medium'
  },
  {
    title: 'AI-Powered Analytics Platform',
    description: 'Developed an enterprise-scale analytics platform using machine learning for predictive insights. Integrated multiple data sources and implemented real-time processing.',
    image: '/images/analytics.jpg',
    technologies: ['Python', 'TensorFlow', 'AWS', 'Apache Spark', 'Kafka', 'Azure','CI/CD']
  },
  {
    title: 'Cloud Migration Framework',
    description: 'Created a comprehensive framework for migrating legacy systems to cloud infrastructure. Implemented automated deployment pipelines and monitoring.',
    image: '/images/cloud.jpg',
    technologies: ['AWS', 'Docker', 'Kubernetes', 'Jenkins', 'Grafana', 'Databricks','CI/CD']
  },
  {
    title: 'Portfolio Website',
    description: 'A Netflix-inspired portfolio website showcasing projects and skills. Features smooth animations and responsive design.',
    image: '/images/portfolio.jpg',
    technologies: ['React', 'TypeScript', 'Node.js', 'CSS','HTML','CI/CD']
  },
  {
    title: 'Project Quantum',
    description: 'Confidential project involving cutting-edge quantum computing applications in financial technology. Revolutionizing transaction processing and security protocols.',
    image: 'https://picsum.photos/seed/quantum/400/225',
    technologies: ['Quantum Computing', 'Pennylane', 'Advanced Cryptography'],
    isNDA: true,
    confidentialityLevel: 'high'
  },
  {
    title: 'Operation Phoenix',
    description: 'Healthcare AI system under NDA, using neural networks for real-time clinical decision support and predictive healthcare.',
    image: 'https://picsum.photos/seed/phoenix/400/225',
    technologies: ['Neural Networks', 'Healthcare APIs', 'Real-time Processing'],
    isNDA: true,
    confidentialityLevel: 'high'
  },
  {
    title: 'Project Atlas',
    description: 'Infrastructure project under NDA, using machine learning for critical systems monitoring and predictive maintenance.',
    image: 'https://picsum.photos/seed/atlas/400/225',
    technologies: ['Machine Learning', 'IoT', 'Advanced Analytics'],
    isNDA: true,
    confidentialityLevel: 'medium'
  }
];

const Projects: React.FC = () => {
  return (
    <div className="projects-container">
      <div className="projects-grid">
        {projects.map((project, index) => (
          <motion.div
            key={project.title}
            className={`project-card ${project.isNDA ? 'nda-card' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="project-image-container">
              <img
                src={project.image}
                alt={`${project.title} - MLOps and AI business solutions project by Uchenna Anyaoha`}
                className="project-image"
                style={project.isNDA ? { filter: 'brightness(0.7) blur(2px)' } : {}}
              />
              {project.isNDA && (
                <div className="nda-overlay">
                  <FaLock className="nda-icon" />
                  <span className="nda-badge">
                    <FaShieldAlt /> Confidential
                  </span>
                </div>
              )}
            </div>
            <div className="project-content">
              <h3 className="project-title">
                {project.title}
                {project.isNDA && (
                  <span className={`confidentiality-level ${project.confidentialityLevel}`}>
                    {project.confidentialityLevel}
                  </span>
                )}
              </h3>
              <p className="project-description">{project.description}</p>
              {project.link && (
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#e50914', fontWeight: 600, textDecoration: 'none' }}
                >
                  View project →
                </a>
              )}
              <div className="project-tech">
                {project.technologies.map(tech => {
                  const icon = getTechIcon(tech);
                  return (
                    <div key={tech} className="tech-icon">
                      <img src={icon.path} alt={`${tech} technology used in MLOps and AI solutions`} />
                      <span>{tech}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Projects;
